import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import logo from "@/assets/logo.png";

const BG = "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHRleHR1cmUlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3Njg5MDA5fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = mode === "login" ? await login(email, password, remember) : await register(name, email, password);
    setLoading(false);
    if (!res.ok) toast.error(res.error);
  };

  return (
    <div className="flex h-screen w-full">
      <div className="relative hidden w-1/2 lg:block">
        <img src={BG} alt="architecture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-zinc-900/40" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="inline-flex items-center justify-center bg-white/95 px-3 py-2 shadow-sm">
            <img src={logo} alt="BizOps" className="h-10 w-auto object-contain" data-testid="login-logo" />
          </div>
          <h2 className="mt-6 max-w-md font-heading text-4xl font-bold leading-tight tracking-tight text-white">
            Run your entire operation from one place.
          </h2>
          <p className="mt-3 max-w-sm text-sm text-zinc-200">
            Projects, tasks, products and approvals — organized, logged and always visible.
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <img src={logo} alt="BizOps" className="h-8 w-auto object-contain" data-testid="login-logo-mobile" />
            <span className="font-heading text-lg font-bold">BizOps</span>
          </div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#b32233]">
            {mode === "login" ? "Welcome back" : "Get started"}
          </p>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-zinc-900">
            {mode === "login" ? "Sign in to BizOps" : "Create your account"}
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Full name</label>
                <input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#b32233] focus:ring-1 focus:ring-[#b32233]"
                  placeholder="Jane Doe"
                />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#b32233] focus:ring-1 focus:ring-[#b32233]"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Password</label>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#b32233] focus:ring-1 focus:ring-[#b32233]"
                placeholder="••••••••"
              />
            </div>
            {mode === "login" && (
              <label className="flex items-center gap-2 text-sm text-zinc-600" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 accent-[#b32233]"
                />
                Keep me signed in for 30 days
              </label>
            )}
            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 bg-[#b32233] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#8f1a28] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-zinc-500">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-medium text-[#b32233] hover:underline"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
